import { ReactNode, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { auth0OnboardingApiRef, Auth0Credentials } from '../api';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
  },
  cardHeader: {
    paddingBottom: 0,
  },
  tenantChip: {
    marginLeft: theme.spacing(1),
    fontWeight: 600,
  },
  body: {
    padding: theme.spacing(2, 0),
  },
  credentialField: {
    marginBottom: theme.spacing(2),
  },
  copyButton: {
    marginLeft: theme.spacing(1),
  },
  fieldLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  fieldValue: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    padding: theme.spacing(1),
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.04)',
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    wordBreak: 'break-all',
  },
}));

interface CredentialFieldProps {
  label: string;
  value: string;
  onCopy: () => void;
}

const CredentialField = ({ label, value, onCopy }: CredentialFieldProps) => {
  const classes = useStyles();

  return (
    <div className={classes.credentialField}>
      <Typography variant="body2" className={classes.fieldLabel}>
        {label}
      </Typography>
      <div className={classes.fieldValue}>
        <span>{value}</span>
        <Tooltip title="Copy to clipboard">
          <IconButton
            size="small"
            onClick={onCopy}
            className={classes.copyButton}
          >
            <FileCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

interface CredentialsBodyProps {
  appName: string;
  auth0Tenant: string;
  credentials: Auth0Credentials;
}

const CredentialsBody = ({
  appName,
  auth0Tenant,
  credentials,
}: CredentialsBodyProps) => {
  const classes = useStyles();
  const api = useApi(auth0OnboardingApiRef);
  const [secretVisible, setSecretVisible] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleRevealToggle = async () => {
    if (secretVisible) {
      setSecretVisible(false);
      return;
    }
    if (revealedSecret !== null) {
      setSecretVisible(true);
      return;
    }
    setRevealLoading(true);
    setRevealError(null);
    try {
      const secret = await api.revealSecret(appName, auth0Tenant);
      setRevealedSecret(secret);
      setSecretVisible(true);
    } catch (e) {
      setRevealError((e as Error).message);
    } finally {
      setRevealLoading(false);
    }
  };

  return (
    <div className={classes.body}>
      <CredentialField
        label="Client ID"
        value={credentials.clientId}
        onCopy={() => copyToClipboard(credentials.clientId)}
      />
      <CredentialField
        label="Domain"
        value={credentials.domain}
        onCopy={() => copyToClipboard(credentials.domain)}
      />
      <CredentialField
        label="API Identifier"
        value={credentials.apiIdentifier}
        onCopy={() => copyToClipboard(credentials.apiIdentifier)}
      />

      <div className={classes.credentialField}>
        <Typography variant="body2" className={classes.fieldLabel}>
          Client Secret
        </Typography>
        <div className={classes.fieldValue}>
          {secretVisible && revealedSecret !== null ? (
            <span>{revealedSecret || '(empty — SPA public client)'}</span>
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <VpnKeyIcon color="action" fontSize="small" />
              <Typography variant="body2" component="span">
                {revealedSecret === null
                  ? 'Stored securely in Azure Key Vault'
                  : '••••••••••••••••'}
              </Typography>
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {revealLoading ? (
              <CircularProgress size={16} style={{ margin: '0 8px' }} />
            ) : (
              <Tooltip title={secretVisible ? 'Hide secret' : 'Show secret'}>
                <IconButton
                  size="small"
                  onClick={handleRevealToggle}
                  className={classes.copyButton}
                >
                  {secretVisible ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {secretVisible && revealedSecret && (
              <Tooltip title="Copy to clipboard">
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(revealedSecret)}
                  className={classes.copyButton}
                >
                  <FileCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </span>
        </div>
        {revealError && (
          <Typography
            variant="caption"
            color="error"
            style={{ marginTop: 4, display: 'block' }}
          >
            {revealError}
          </Typography>
        )}
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ marginTop: 4, display: 'block' }}
        >
          Secret name: <code>{credentials.secretName}</code>
        </Typography>
      </div>
    </div>
  );
};

/**
 * Card component that displays Auth0 credentials for an entity.
 *
 * Onboarding creates one Auth0 client per deployment environment the app
 * selects, each in that environment's tenant. The backend works out those
 * tenants from the app's stored form snapshot, and the card renders one
 * credentials view per tenant.
 */
export const Auth0CredentialsCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const api = useApi(auth0OnboardingApiRef);

  const appName = entity.metadata.name;

  const credentials = useAsync(
    () => api.getApplicationCredentials(appName),
    [appName],
  );

  let bodyContent: ReactNode;
  if (credentials.loading) {
    bodyContent = (
      <div className={classes.body}>
        <Skeleton variant="rect" height={40} style={{ marginBottom: 16 }} />
        <Skeleton variant="rect" height={40} style={{ marginBottom: 16 }} />
        <Skeleton variant="rect" height={40} style={{ marginBottom: 16 }} />
        <Skeleton variant="rect" height={40} />
      </div>
    );
  } else if (credentials.error) {
    bodyContent = (
      <div className={classes.body}>
        <Alert severity="error">
          Failed to fetch Auth0 credentials: {credentials.error.message}
        </Alert>
      </div>
    );
  } else if (!credentials.value?.auth0Enabled) {
    bodyContent = (
      <div className={classes.body}>
        <Alert severity="info">
          Auth0 is not enabled for this application.
        </Alert>
      </div>
    );
  } else {
    bodyContent = credentials.value.tenants.map((tenantCredentials, index) => (
      <div key={tenantCredentials.tenant}>
        {index > 0 && <Divider />}
        <div className={classes.body}>
          <Chip
            size="small"
            label={`Auth0 tenant: ${tenantCredentials.tenant}`}
            className={classes.tenantChip}
          />
        </div>
        {tenantCredentials.found && tenantCredentials.reason && (
          <div className={classes.body}>
            <Alert severity="warning">
              The latest onboarding of this tenant failed:{' '}
              {tenantCredentials.reason.replace(/\.$/, '')}.
            </Alert>
          </div>
        )}
        {tenantCredentials.found && (
          <CredentialsBody
            appName={appName}
            auth0Tenant={tenantCredentials.tenant}
            credentials={tenantCredentials}
          />
        )}
        {!tenantCredentials.found && tenantCredentials.status === 'waiting' && (
          <div className={classes.body}>
            <Alert severity="info">
              Onboarding to the <strong>{tenantCredentials.tenant}</strong>{' '}
              tenant is waiting for approval or still running.
            </Alert>
          </div>
        )}
        {!tenantCredentials.found && tenantCredentials.status === 'failed' && (
          <div className={classes.body}>
            <Alert severity="warning">
              Onboarding to the <strong>{tenantCredentials.tenant}</strong>{' '}
              tenant failed: {tenantCredentials.reason?.replace(/\.$/, '')}.
            </Alert>
          </div>
        )}
        {!tenantCredentials.found &&
          tenantCredentials.status !== 'waiting' &&
          tenantCredentials.status !== 'failed' && (
          <div className={classes.body}>
            <Alert severity="info">
              No Auth0 credentials found for this app in the{' '}
              <strong>{tenantCredentials.tenant}</strong> tenant. No finished
              onboarding is recorded for it.
            </Alert>
          </div>
        )}
      </div>
    ));
  }

  return (
    <Card className={classes.card}>
      <CardHeader
        title="Auth0 Credentials"
        className={classes.cardHeader}
        subheader="Auth0 application credentials"
      />
      <Divider />
      <CardContent>{bodyContent}</CardContent>
    </Card>
  );
};
